import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, Copy, ExternalLink, QrCode, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function QRPanel({ title, color, accent, config, kind, onCopy, envVarName, downloadColor }) {
  // Bust cache when destination env flips so admins see the latest QR after
  // updating env vars (each PNG is small — ~30KB labelled, ~4KB bare — so re-fetch is cheap).
  const [bust, setBust] = useState(() => Date.now());
  // Labelled = title + SCC logo + URL footer (print-ready).
  // Bare = pure QR (for designers composing their own poster).
  const [labelled, setLabelled] = useState(true);
  // Logo colour: "bw" (default, prints cleanly on any printer) or "yellow"
  // (original brand lion, for colour-printed or digital posters).
  const [colour, setColour] = useState("bw");

  // qr_image_url is relative ("/api/...") — prepend the configured backend
  // origin so the browser fetches from the same host the admin is on.
  const baseImageUrl = config.qr_image_url.startsWith("http")
    ? config.qr_image_url
    : `${BACKEND_URL}${config.qr_image_url}`;
  const absoluteImageUrl = `${baseImageUrl}?labelled=${labelled ? "true" : "false"}&colour=${colour}`;
  const imgUrl = `${absoluteImageUrl}&t=${bust}`;
  const colourSuffix = colour === "yellow" ? "-yellow" : "";
  const downloadName = `scc-${kind}-master-qr${labelled ? "" : "-bare"}${colourSuffix}.png`;

  const handleDownload = async () => {
    try {
      const resp = await axios.get(absoluteImageUrl, { responseType: "blob", withCredentials: false });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${downloadName}`);
    } catch (err) {
      console.error(err);
      toast.error("Download failed");
    }
  };

  return (
    <Card className={`bg-slate-900/60 border-slate-800 p-6 ${color}`} data-testid={`master-qr-card-${kind}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className={`text-xl font-semibold text-white flex items-center gap-2`}>
            <QrCode className={`w-5 h-5 ${accent}`} /> {title}
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Scan routes parents through the Portal to the Academy Site marketing page.
          </p>
        </div>
        {config.destination_env_set ? (
          <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 whitespace-nowrap">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Env configured
          </Badge>
        ) : (
          <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 whitespace-nowrap">
            <AlertTriangle className="w-3 h-3 mr-1" /> Using fallback
          </Badge>
        )}
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6 items-start">
        <div className="bg-white p-3 rounded-lg flex items-center justify-center">
          <img
            src={imgUrl}
            alt={`${title} master QR`}
            className="w-full h-auto"
            data-testid={`master-qr-img-${kind}`}
          />
        </div>

        <div className="space-y-3 min-w-0">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Encoded inside QR</div>
            <div className="flex items-center gap-2">
              <code className="text-xs text-slate-300 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 truncate flex-1" data-testid={`master-qr-encoded-${kind}`}>
                {config.encoded_url}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => onCopy(config.encoded_url, "Encoded URL")}
                data-testid={`master-qr-copy-encoded-${kind}`}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Redirects to</div>
            <div className="flex items-center gap-2">
              <code className="text-xs text-slate-300 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 truncate flex-1" data-testid={`master-qr-dest-${kind}`}>
                {config.destination}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => window.open(config.destination, "_blank")}
                data-testid={`master-qr-open-dest-${kind}`}
              >
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {!config.destination_env_set && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
              <strong className="text-amber-300">Heads up:</strong> The <code className="text-amber-100">{envVarName}</code> env var is not set, so scans fall back to a sensible default. Set this in backend <code className="text-amber-100">.env</code> to point to the live destination.
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs uppercase tracking-wide text-slate-500">Style</span>
            <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid={`master-qr-style-${kind}`}>
              <button
                type="button"
                onClick={() => setLabelled(true)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${labelled ? "bg-slate-100 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                data-testid={`master-qr-style-labelled-${kind}`}
              >
                Labelled (with logo)
              </button>
              <button
                type="button"
                onClick={() => setLabelled(false)}
                className={`px-3 py-1 text-xs font-medium transition-colors border-l border-slate-700 ${!labelled ? "bg-slate-100 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                data-testid={`master-qr-style-bare-${kind}`}
              >
                Bare QR
              </button>
            </div>
          </div>

          {labelled && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs uppercase tracking-wide text-slate-500">Logo</span>
              <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid={`master-qr-colour-${kind}`}>
                <button
                  type="button"
                  onClick={() => setColour("bw")}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${colour === "bw" ? "bg-slate-100 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                  data-testid={`master-qr-colour-bw-${kind}`}
                >
                  Black &amp; White
                </button>
                <button
                  type="button"
                  onClick={() => setColour("yellow")}
                  className={`px-3 py-1 text-xs font-medium transition-colors border-l border-slate-700 ${colour === "yellow" ? "bg-yellow-400 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                  data-testid={`master-qr-colour-yellow-${kind}`}
                >
                  Yellow lion
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              className={`${downloadColor || "bg-purple-600 hover:bg-purple-500"} text-white`}
              onClick={handleDownload}
              data-testid={`master-qr-download-${kind}`}
            >
              <Download className="w-4 h-4 mr-1" /> Download PNG
            </Button>
            <Button
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => setBust(Date.now())}
              data-testid={`master-qr-refresh-${kind}`}
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh image
            </Button>
            {(kind === "kit" || kind === "academy") && (
              <Button
                variant="outline"
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                onClick={() => {
                  const url = `${BACKEND_URL}/api/events/print-card/${kind}?colour=${colour}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                data-testid={`master-qr-print-card-${kind}`}
                title="Open a printable A5 noticeboard card with the QR and step-by-step instructions"
              >
                🖨️ Print A5 card
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function AdminMasterQRs() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/events/master-qrs/config`, { withCredentials: true });
      setConfig(resp.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load master QR config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="master-qrs-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin/events" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="master-qrs-back">
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <QrCode className="w-7 h-7 text-purple-400" /> Master QRs
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Printable QR codes for the club noticeboard, registration desk, or flyers. Camps, Tournaments,
              Free Trial, Kit Ordering, and Academy Registration each have a single QR that always points to its
              current destination — re-print only if the underlying brand URL changes.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchConfig}
            disabled={loading}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="master-qrs-refresh"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading && !config ? (
          <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>
        ) : !config ? (
          <Card className="bg-slate-900/60 border-slate-800 p-10 text-center text-slate-400">
            Could not load configuration.
          </Card>
        ) : (
          <div className="space-y-6">
            <QRPanel
              title="Camps"
              color=""
              accent="text-orange-400"
              config={config.camps}
              kind="camps"
              onCopy={copy}
              envVarName="CAMPS_HUB_URL"
              downloadColor="bg-orange-600 hover:bg-orange-500"
            />
            <QRPanel
              title="Tournaments"
              color=""
              accent="text-purple-400"
              config={config.tournaments}
              kind="tournaments"
              onCopy={copy}
              envVarName="TOURNAMENTS_HUB_URL"
              downloadColor="bg-purple-600 hover:bg-purple-500"
            />
            {config.trial && (
              <QRPanel
                title="Free Trial"
                color=""
                accent="text-emerald-400"
                config={config.trial}
                kind="trial"
                onCopy={copy}
                envVarName="TRIAL_HUB_URL"
                downloadColor="bg-emerald-600 hover:bg-emerald-500"
              />
            )}
            {config.kit && (
              <QRPanel
                title="Kit Ordering"
                color=""
                accent="text-amber-400"
                config={config.kit}
                kind="kit"
                onCopy={copy}
                envVarName="KIT_HUB_URL"
                downloadColor="bg-amber-600 hover:bg-amber-500"
              />
            )}
            {config.academy && (
              <QRPanel
                title="Academy Registration"
                color=""
                accent="text-sky-400"
                config={config.academy}
                kind="academy"
                onCopy={copy}
                envVarName="ACADEMY_HUB_URL"
                downloadColor="bg-sky-600 hover:bg-sky-500"
              />
            )}

            <Card className="bg-slate-900/40 border-slate-800 p-5 text-sm text-slate-300">
              <h3 className="font-semibold text-white mb-2">How this works</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li><strong className="text-slate-200">Labelled (default)</strong> includes a title (Camps / Tournaments / Free Trial / Kit Ordering), the SCC logo in the centre, and the destination URL as a human-readable footer — print-ready as-is.</li>
                <li><strong className="text-slate-200">Bare QR</strong> is just the code, for designers composing their own poster.</li>
                <li><strong className="text-slate-200">Logo colour</strong> — pick <strong>Black &amp; White</strong> (default, prints flawlessly on any printer) or the original <strong>Yellow lion</strong> for colour-printed posters and digital sharing.</li>
                <li>When a parent scans, their phone hits the Portal redirect URL, which 302-redirects to the configured destination. <code className="text-slate-300">?src=qr</code> is forwarded so analytics can attribute scans.</li>
                <li>Destinations are controlled by the backend env vars <code className="text-slate-300">CAMPS_HUB_URL</code>, <code className="text-slate-300">TOURNAMENTS_HUB_URL</code>, <code className="text-slate-300">TRIAL_HUB_URL</code>, <code className="text-slate-300">KIT_HUB_URL</code>, and <code className="text-slate-300">ACADEMY_HUB_URL</code>. Change those in <code className="text-slate-300">/app/backend/.env</code> and the QR keeps working — no re-print needed.</li>
                <li>Printing on a black-and-white printer is fine — the SCC logo is rendered in pure black.</li>
                <li>If an env var is missing, scans fall back to a sensible Portal page so the QR never lands on a 404.</li>
              </ul>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
